/**
 * Article Content Manager - Moon Moon Agency
 * Affiche le texte alt des images du contenu d'article sous chaque image
 */

/*********************************************************************************************
 * 🖼️ ARTICLE CONTENT MANAGER
 */
const ArticleContentManager = {
	config: {
		selectors: {
			container: '.mm-article-content',
			image: '.mm-article-content img[alt]',
			videoWrapper: '.mm-article-content-video-wrapper',
			videoPlayButton: '[data-article-video-play]',
			video: '[data-article-video]',
		},
		classes: {
			caption: 'mm-article-content__image-caption',
			playing: 'mm-is-playing',
		},
	},

	/**
	 * Initialize the module
	 */
	init() {
		const container = window.getElement(this.config.selectors.container);
		if (!container) return; // Guard clause : sortie silencieuse si absent du DOM

		this._addCaptions(container);
		this._bindVideoEvents(container);
		window.log('info', '🖼️ [ARTICLE_CONTENT]', 'Initialized');
	},

	_bindVideoEvents(container) {
		container.addEventListener('click', (e) => {
			const button = e.target.closest(this.config.selectors.videoPlayButton);
			if (!button) return;

			const wrapper = button.closest(this.config.selectors.videoWrapper);
			const video = wrapper?.querySelector(this.config.selectors.video);
			if (!video) return;

			video.controls = true;
			video.play();
			wrapper.classList.add(this.config.classes.playing);
		});
	},

	_addCaptions(container) {
		const images = container.querySelectorAll(this.config.selectors.image);

		images.forEach((image) => {
			const altText = image.getAttribute('alt').trim();
			if (!altText) return;
			if (image.nextElementSibling?.classList.contains(this.config.classes.caption)) return;

			const caption = document.createElement('span');
			caption.className = this.config.classes.caption;
			caption.textContent = altText;
			image.insertAdjacentElement('afterend', caption);
		});
	},
};

/*********************************************************************************************
 * 🗂️ ARTICLE TOC MANAGER
 * Sommaire mobile sticky genere depuis les H2 de l'article
 */
const ArticleTocManager = {
	config: {
		selectors: {
			content: '.mm-article-content',
			toc: '[data-mm-article-toc]',
			trigger: '[data-mm-article-toc-trigger]',
			panel: '[data-mm-article-toc-panel]',
			list: '[data-mm-article-toc-list]',
			backdrop: '[data-mm-article-toc-backdrop]',
		},
		classes: {
			ready: 'is-ready',
			open: 'is-open',
			active: 'is-active',
			item: 'mm-article-toc__item',
			link: 'mm-article-toc__link',
			linkText: 'mm-article-toc__link-text',
		},
	},

	/**
	 * Initialize the module
	 */
	init() {
		const content = window.getElement(this.config.selectors.content);
		const toc = window.getElement(this.config.selectors.toc);
		if (!content || !toc) return; // Guard clause : sortie silencieuse si absent du DOM

		const headings = content.querySelectorAll('h2');
		if (!headings.length) return;

		this.toc = toc;
		this.trigger = toc.querySelector(this.config.selectors.trigger);
		this.panel = toc.querySelector(this.config.selectors.panel);
		this.list = toc.querySelector(this.config.selectors.list);
		this.backdrop = content.querySelector(this.config.selectors.backdrop);
		this.headingLinkMap = new Map();
		this.activeLink = null;

		this._buildList(headings);
		this._bindEvents();
		this._setupScrollSpy(headings);

		toc.classList.add(this.config.classes.ready);
		window.log('info', '🗂️ [ARTICLE_TOC]', 'Initialized', { headings: headings.length });
	},

	_buildList(headings) {
		const usedIds = new Set();

		headings.forEach((heading, index) => {
			if (!heading.id) {
				heading.id = this._slugify(heading.textContent, index, usedIds);
			}
			usedIds.add(heading.id);

			const item = document.createElement('li');
			item.className = this.config.classes.item;

			const link = document.createElement('a');
			link.className = this.config.classes.link;
			link.href = `#${heading.id}`;

			const linkText = document.createElement('span');
			linkText.className = this.config.classes.linkText;
			linkText.textContent = heading.textContent.trim();
			link.appendChild(linkText);

			item.appendChild(link);
			this.list.appendChild(item);

			this.headingLinkMap.set(heading, link);
		});
	},

	_setupScrollSpy(headings) {
		if (typeof IntersectionObserver === 'undefined') return;

		this._scrollSpyHeadings = Array.from(headings);
		const headerOffset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height-mobile'), 10) || 0;
		this._scrollSpyLineOffset = headerOffset + 25;

		const observer = new IntersectionObserver(() => this._updateActiveHeading(), {
			rootMargin: `-${this._scrollSpyLineOffset}px 0px 0px 0px`,
		});
		this._scrollSpyHeadings.forEach((heading) => observer.observe(heading));

		this._updateActiveHeading();
	},

	// Le titre actif est le dernier dont le haut a franchi la ligne de lecture (pas seulement celui visible dans une bande etroite)
	_updateActiveHeading() {
		let current = this._scrollSpyHeadings[0];
		this._scrollSpyHeadings.forEach((heading) => {
			if (heading.getBoundingClientRect().top - this._scrollSpyLineOffset <= 0) current = heading;
		});
		this._setActiveHeading(current);
	},

	_setActiveHeading(heading) {
		const link = this.headingLinkMap.get(heading);
		if (!link || link === this.activeLink) return;

		this.activeLink?.classList.remove(this.config.classes.active);
		link.classList.add(this.config.classes.active);
		this.activeLink = link;
	},

	_slugify(text, index, usedIds) {
		const base =
			text
				.trim()
				.toLowerCase()
				.normalize('NFD')
				.replace(/[̀-ͯ]/g, '')
				.replace(/[^a-z0-9]+/g, '-')
				.replace(/(^-|-$)/g, '') || `section-${index}`;

		let id = `mm-toc-${base}`;
		let suffix = 1;
		while (usedIds.has(id)) {
			id = `mm-toc-${base}-${suffix}`;
			suffix += 1;
		}
		return id;
	},

	_bindEvents() {
		this.trigger.addEventListener('click', () => this.toggle());

		this.backdrop.addEventListener('click', () => this.close());

		this.toc.addEventListener('keydown', (e) => {
			if (e.key !== 'Escape' || !this.toc.classList.contains(this.config.classes.open)) return;
			this.close();
			this.trigger.focus();
		});

		this.list.addEventListener('click', (e) => {
			const link = e.target.closest(`.${this.config.classes.link}`);
			if (!link) return;

			e.preventDefault();
			const target = document.getElementById(link.getAttribute('href').slice(1));
			if (!target) return;

			this.close();
			target.scrollIntoView({ behavior: 'smooth', block: 'start' });
		});
	},

	toggle() {
		this.toc.classList.contains(this.config.classes.open) ? this.close() : this.open();
	},

	open() {
		this._updateActiveHeading();
		this.toc.classList.add(this.config.classes.open);
		this.backdrop.classList.add(this.config.classes.open);
		this.trigger.setAttribute('aria-expanded', 'true');
		this._lockScroll();
	},

	close() {
		this.toc.classList.remove(this.config.classes.open);
		this.backdrop.classList.remove(this.config.classes.open);
		this.trigger.setAttribute('aria-expanded', 'false');
		this._unlockScroll();
	},

	_lockScroll() {
		this.scrollY = window.scrollY;
		document.body.style.position = 'fixed';
		document.body.style.top = `-${this.scrollY}px`;
		document.body.style.width = '100%';
	},

	_unlockScroll() {
		document.body.style.position = '';
		document.body.style.top = '';
		document.body.style.width = '';
		window.scrollTo(0, this.scrollY || 0);
	},
};

// Auto-init avec vérification readyState
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => {
		ArticleContentManager.init();
		ArticleTocManager.init();
	});
} else {
	ArticleContentManager.init();
	ArticleTocManager.init();
}
